<template>
  <hr-form-dialog
    ref="dialog"
    size="md"
    :title="program.programName"
    :minimize="(!editData || isDraft) && !isDataFieldsEmpty"
    @close="onDialogHide"
    @hide="onDialogHide"
    @minimize="onSave('draft')"
    @submit="onSave('complete')"
  >
    <div class="q-col-gutter-sm col-12 row items-center">
      <div class="col">
        <hr-time-range-field
          ref="timeRangeInput"
          v-model:start-time="startTime"
          v-model:end-time="endTime"
          :from-label="$t('programs.behaviour.behaviourProgram.inputStartTimeLabel')"
          :to-label="$t('programs.behaviour.behaviourProgram.inputEndTimeLabel')"
          :time-zone="currentIANATimezone"
          hide-bottom-space
          @focus="onStop"
        >
          <template #start-prepend>
            <q-icon
              name="event"
              size="xs"
              class="cursor-pointer q-mr-xs"
              @click.stop="openStartDatePicker"
            >
              <q-popup-proxy
                ref="startDate"
                no-parent-event
                transition-show="scale"
                transition-hide="scale"
              >
                <hr-date
                  v-model="selectStartDate"
                  @update:model-value="onChangeStartDate"
                />
              </q-popup-proxy>
            </q-icon>
          </template>
        </hr-time-range-field>
      </div>
      <div
        v-if="hasTimer"
        class="col-auto"
      >
        <hr-subtle-btn
          v-if="!timerRunning"
          :label="$q.screen.gt.xs ? $t('programs.behaviour.behaviourProgram.buttonRestart') : ''"
          icon="play_circle_outline"
          size="sm"
          @click="onStart"
        />
        <hr-subtle-btn
          v-else
          :label="$q.screen.gt.xs ? $t('programs.behaviour.behaviourProgram.buttonStop') : ''"
          icon="stop"
          size="sm"
          @click="onStop"
        />
      </div>
      <div
        v-for="group in groups"
        :key="group.id"
        class="col-12"
      >
        <behaviour-group-select
          v-model="group.selectedOptions"
          :label="group.label"
          :program="program"
          :group="group"
        />
      </div>
      <hr-text-field
        v-model="notes"
        :label="$t('programs.behaviour.behaviourProgram.inputNotesLabel')"
        multiline
        hide-bottom-space
        class="col-12"
      />
    </div>
    <template #destructive-actions>
      <hr-destructive-btn
        v-if="abcDraft && isDraft"
        icon="delete"
        :loading="isSaving"
        :disable="isSaving"
        :label="$t('common.deleteDraft')"
        @click="onDeleteDraftData"
      />
    </template>
    <template #actions>
      <hr-confirm-btn
        :label="$t('programs.behaviour.behaviourProgram.buttonSaveEntry')"
        :loading="isSaving"
        :disable="isDataFieldsEmpty || isSaving"
        @click="onSave('complete')"
      />
    </template>
  </hr-form-dialog>
</template>
<script>
import BehaviourGroupSelect from 'components/Programs/Behaviour/BehaviourGroupSelect';
import { Timestamp } from 'firebase/firestore';
import {
  chain, find, flatten, get, map, omit, size,
} from 'lodash';
import { mapActions, mapState } from 'pinia';
import { notifyNegative, notifySuccess } from 'src/composables/interactions';
import {
  HrConfirmBtn, HrDate, HrDestructiveBtn, HrFormDialog, HrSubtleBtn, HrTextField, HrTimeRangeField,
} from 'src/design-system';
import { useTime } from 'src/composables/time';
import DataDB from 'src/fb/data-db';
import { auth } from 'src/store/auth';
import { programBehaviourData } from 'src/store/programBehaviourData';

export default {
  name: 'BehaviourProgram',

  components: {
    BehaviourGroupSelect,
    HrConfirmBtn,
    HrDate,
    HrDestructiveBtn,
    HrFormDialog,
    HrSubtleBtn,
    HrTextField,
    HrTimeRangeField,
  },

  props: {
    program: {
      type: Object,
      required: true,
    },

    editData: {
      type: Object,
      required: false,
      default: null,
    },

    session: {
      type: Object,
      required: false,
      default: null,
    },
  },

  emits: ['hide', 'ok'],

  setup() {
    const {
      extractDate, formatDate, addToDate, currentIANATimezone,
    } = useTime();
    return {
      extractDate, formatDate, addToDate, currentIANATimezone,
    };
  },

  data() {
    return {
      isSaving: false,

      startTime: Timestamp.now(),
      endTime: Timestamp.now(),

      timerRunning: false,
      timerInterval: null,

      notes: '',
      groups: [],

    };
  },

  computed: {
    ...mapState(auth, ['userDetails']),
    ...mapState(programBehaviourData, ['abcDraft', 'programBehaviourGroups', 'programBehaviourGroupItems']),

    hasTimer() {
      return this.editData === null;
    }, // hasTimer()

    editStartTime() {
      return this.startTime;
      // return this.formatDate(this.startTime.toDate(), 'HH:mm');
    }, // editStartTime()

    editEndTime() {
      return this.formatDate(this.endTime.toDate(), 'HH:mm');
    }, // editEndTime()

    availableItemsFiltered() {
      return chain(this.groups).keyBy('id').mapValues((group) => group.items).value();
    }, // availableItemsFiltered()

    isDraft() {
      return get(this.editData, 'status', null) === 'draft';
    }, // isDraft()

    isDataFieldsEmpty() {
      return (chain(this.groups)
        .filter(({ selectedOptions }) => size(selectedOptions) !== 0)
        .size()
        .value() === 0
        && size(this.notes) === 0);
    }, // isDataFieldsEmpty()

    selectStartDate: {
      get() {
        if (!(this.startTime instanceof Timestamp)) {
          return null;
        }

        return this.formatDate(this.startTime.toDate(), 'yyyy/MM/dd');
      },

      set(selectedDate) {
        if (!selectedDate || !(this.startTime instanceof Timestamp)) {
          return;
        }

        if (this.timerRunning) {
          this.stopTimer();
        }

        const currentTime = this.formatDate(this.startTime.toDate(), 'HH:mm');
        const dateString = `${selectedDate} ${currentTime}`;

        this.startTime = Timestamp.fromDate(
          this.extractDate(dateString, 'yyyy/MM/dd HH:mm'),
        );

        if (this.endTime instanceof Timestamp) {
          this.onInputEndTime(this.editEndTime);
        }
      },
    },

  },

  async created() {
    this.isSaving = false;
    await this.loadProgramBehaviourDataGroups({ programId: this.program.id, loadItems: true });
    // Make sure we include deleted items if they are selected in editData
    const editGroups = chain(this.editData)
      .get('data')
      .map('behaviourGroup')
      .uniq()
      .value();
    const editItems = chain(this.editData)
      .get('data')
      .map('behaviourItem')
      .uniq()
      .value();

    this.groups = chain(this.programBehaviourGroups)
      .map(
        (group) => ({
          selectedOptions: [],
          ...group,
          items: chain(this.programBehaviourGroupItems)
            .get(group.id, [])
            .map((item) => item) // Convert from object to array
            .reject((item) => (item.isDeleted && (!editItems.includes(item.id))))
            .sortBy('order')
            .value(),
        }),
      )
      .reject((group) => (group.isDeleted && (!editGroups.includes(group.id))))
      .value();

    if ((this.editData !== null) && (this.editData.dataType === 'Behaviour1.0')) {
      this.startTime = this.isDraft
        ? Timestamp.fromDate(new Date(this.editData.startTime.seconds * 1000))
        : this.editData.startTime;
      this.endTime = this.isDraft
        ? Timestamp.now()
        : this.editData.endTime;
      this.notes = this.editData.notes;

      // Load saved data into groups
      this.groups.forEach((group, key) => {
        this.groups[key].selectedOptions = chain(this.editData.data)
          .filter({ behaviourGroup: group.id })
          .map((item) => (find(group.items, { id: item.behaviourItem })))
          .value();
      });
    } else {
      this.startTimer();
    }
  },

  beforeUnmount() {
    this.stopTimer();
  },

  methods: {
    ...mapActions(
      programBehaviourData,
      ['loadProgramBehaviourDataGroups', 'addProgramBehaviourDataItem', 'setAbcDraft', 'removeCurrentDraft'],
    ),

    show() {
      this.$refs.dialog.show();
    },

    hide() {
      if (this.$refs.dialog) {
        this.$refs.dialog.hide();
      }
    },

    onDialogHide() {
      // required to be emitted
      // when QDialog emits "hide" event
      this.$emit('hide');
    },

    onStart() {
      this.startTimer();
    }, // onStart

    onStop() {
      this.stopTimer();
    }, // onStop()

    openStartDatePicker() {
      this.$refs.startDate.show();
    },

    onChangeStartDate() {
      this.$refs.startDate.hide();
    }, // onChangeStartDate()

    onSelectOption(index) {
      this.$refs.groups[index].hidePopup();
    }, // onSelectOption()

    onInputEndTime(d) {
      if (this.timerRunning) {
        this.stopTimer();
      }

      const dateString = `${this.formatDate(this.startTime.toDate(), 'yyyy-MM-dd')} ${d}`;
      this.endTime = Timestamp.fromDate(this.extractDate(dateString, 'yyyy-MM-dd HH:mm'));
      if (this.endTime.toMillis() < this.startTime.toMillis()) {
        this.endTime = Timestamp
          .fromDate(this.addToDate(this.endTime.toDate(), { days: 1 }));
      }
    }, // onInputEndTime()

    startTimer() {
      if (this.timerRunning) {
        return;
      }
      this.startTime = Timestamp.now();
      this.timerInterval = setInterval(() => {
        this.endTime = Timestamp.now();
      }, 10);
      this.timerRunning = true;
    },

    stopTimer() {
      if (!this.timerRunning) {
        return;
      }
      this.timerRunning = false;
      clearInterval(this.timerInterval);
    },

    focusTimeField(index) {
      const inputs = this.$refs.timeRangeInput?.$el?.querySelectorAll('input');

      inputs?.[index]?.focus();
    },

    validate() {
      if (!(this.startTime instanceof Timestamp) || this.startTime.toMillis() <= 0) {
        this.focusTimeField(0);
        throw new Error('Invalid start time entered.');
      }

      if (!(this.endTime instanceof Timestamp) || this.endTime.toMillis() <= 0) {
        this.focusTimeField(1);
        throw new Error('Invalid end time entered.');
      }

      if (this.endTime.toMillis() < this.startTime.toMillis()) {
        this.focusTimeField(1);
        throw new Error('End time must be after start time.');
      }
    }, // validate()

    async onSave(status) {
      const dataDb = new DataDB();
      if (this.timerRunning) {
        this.stopTimer();
      }

      try {
        this.validate();
      } catch (err) {
        notifyNegative(err.message);
        return;
      }
      this.isSaving = true;

      const recordedDataActions = chain(this.groups)
        .map(async (group) => Promise.all(map(
          group.selectedOptions,
          async (item) => {
            let behaviourItem = item.id;

            if (get(item, 'editStatus') === 'create') {
              // Create new item
              const newItem = await this.addProgramBehaviourDataItem({
                programId: this.program.id,
                groupId: group.id,
                item: omit(item, ['id', 'editStatus']),
              });
              behaviourItem = newItem.id;
            }
            return {
              behaviourGroup: group.id,
              behaviourItem,
            };
          },
        )))
        .value();
      const recordedData = flatten(await Promise.all(recordedDataActions));

      const dataObj = {
        client: this.program.client,
        clinic: this.userDetails.clinic,
        program: this.program.id,
        sessionProgram: null,
        user: this.userDetails.userId,
        data: recordedData,
        dataType: 'Behaviour1.0',
        notes: this.notes,
        startTime: this.startTime,
        endTime: this.endTime,
        status,
        draftId: status === 'draft' ? this.program.client : null,
      };

      if (status === 'draft') {
        this.setAbcDraft({ abcDraft: dataObj, draftId: this.program.client });
      } else {
        if ((this.editData !== null) && !get(this.editData, 'draftId', null)) {
          await dataDb.set(dataObj, this.editData.id);
        } else {
          dataObj.session = get(this.session, 'id', null);
          await dataDb.create(dataObj);
        }

        if (get(this.editData, 'draftId', null)) {
          this.removeCurrentDraft({ draftId: this.program.client });
        }

        notifySuccess(this.$t('programs.behaviour.behaviourProgram.notifySaveIncidentSuccess'));
      }

      this.$emit('ok');
      this.hide();
    }, // onSave()

    onDeleteDraftData() {
      const editItems = chain(this.editData)
        .get('data')
        .map('behaviourItem')
        .uniq()
        .value();
      this.groups = chain(this.programBehaviourGroups)
        .map(
          (group) => ({
            selectedOptions: [],
            ...group,
            items: chain(this.programBehaviourGroupItems)
              .get(group.id, [])
              .map((item) => item) // Convert from object to array
              .reject((item) => (item.isDeleted && (!editItems.includes(item.id))))
              .sortBy('order')
              .value(),
          }),
        )
        .reject((group) => (group.isDeleted && (!editItems.includes(group.id))))
        .value();
      this.notes = '';
      this.startTime = Timestamp.now();
      this.endTime = Timestamp.now();

      if (this.isDraft) {
        this.removeCurrentDraft({ draftId: this.program.client });
      }
      this.hide();
    }, // onDeleteDraftData()

  },
};
</script>
<style scoped>
.behavior-dialog-card.not-mobile  {
    width:80vw
}
</style>
